import React, { useEffect, useState } from "react";
import { Plus, Trash2, FileText, X, CheckCircle2, Archive, Award, RotateCw, Users as UsersIcon, Clock, DollarSign } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

// ============================================================
// Combined admin panels for the post-Books "extras": Stock Ledger,
// Grants (Incoming + Outgoing), Recurring Subscriptions, Volunteer
// Hours/Skills, Contractor Applications.
// ============================================================

// -------------------- Stock Ledger --------------------
export const StockLedgerPanel = () => {
    const [movements, setMovements] = useState([]);
    const [products, setProducts] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ product_id: "", change: "", reason: "restock", reference: "", notes: "" });

    const load = () => api.get("/admin/stock-movements").then((r) => setMovements(r.data)).catch(() => {});
    useEffect(() => { load(); api.get("/admin/products").then((r) => setProducts(r.data)); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/stock-movements", { ...form, change: parseInt(form.change, 10) });
            toast.success("Stock movement recorded");
            setShow(false);
            setForm({ product_id: "", change: "", reason: "restock", reference: "", notes: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    return (
        <div data-testid="admin-stock-ledger">
            <div className="flex justify-between items-center mb-5">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Stock Ledger</h2>
                    <p className="text-sm text-[#4A5568]">{movements.length} movements · sales recorded automatically</p>
                </div>
                <button onClick={() => setShow(true)} data-testid="add-stock-movement-btn" className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-5 py-2 text-sm font-medium">
                    <Plus className="w-4 h-4" /> Add movement
                </button>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="stock-movements-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr><th className="text-left px-4 py-3 font-medium">Date</th><th className="text-left px-4 py-3 font-medium">Product</th><th className="text-right px-4 py-3 font-medium">Change</th><th className="text-left px-4 py-3 font-medium">Reason</th><th className="text-left px-4 py-3 font-medium">Reference</th><th className="text-right px-4 py-3 font-medium">Stock after</th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {movements.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-[#4A5568]">No movements yet. Sales record automatically when an order is paid.</td></tr>}
                        {movements.map((m) => (
                            <tr key={m.id} data-testid={`stock-move-${m.id}`}>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{(m.created_at || "").slice(0, 10)}</td>
                                <td className="px-4 py-3 text-[#1C252E]">{m.product_name}</td>
                                <td className={`px-4 py-3 text-right font-mono font-medium ${m.change > 0 ? "text-green-700" : "text-red-700"}`}>{m.change > 0 ? "+" : ""}{m.change}</td>
                                <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[#F4F1EA] text-[#4A5568]">{m.reason.replace("_", " ")}</span></td>
                                <td className="px-4 py-3 text-xs text-[#4A5568] font-mono">{m.reference || "—"}</td>
                                <td className="px-4 py-3 text-right font-mono text-[#1C252E]">{m.stock_after}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)} data-testid="stock-movement-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="font-heading text-2xl text-[#1C252E] font-medium">Record movement</h3>
                            <button onClick={() => setShow(false)} className="p-2"><X className="w-4 h-4" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Product <span className="text-[#E53935]">*</span></label>
                                <select required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white" data-testid="stock-product">
                                    <option value="">— Select —</option>
                                    {products.map((p) => <option key={p.id} value={p.id}>{p.name} (stock: {p.stock})</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Change <span className="text-[#E53935]">*</span></label>
                                    <input required type="number" value={form.change} onChange={(e) => setForm({ ...form, change: e.target.value })} placeholder="e.g. 50 or -3" className="w-full rounded-xl border border-black/10 px-4 py-3 font-mono" data-testid="stock-change" />
                                    <p className="text-[11px] text-[#4A5568] mt-1">Positive for additions, negative for removals.</p>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Reason <span className="text-[#E53935]">*</span></label>
                                    <select required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white" data-testid="stock-reason">
                                        <option value="restock">Restock</option>
                                        <option value="write_off">Write-off / damaged</option>
                                        <option value="donation_in">Donation-in (received)</option>
                                        <option value="adjustment">Adjustment</option>
                                    </select>
                                </div>
                            </div>
                            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Reference (PO #, invoice, etc.)" className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="stock-reference" />
                            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" rows={2} className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none" />
                            <button type="submit" data-testid="stock-submit" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3 font-medium">Record movement</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// -------------------- Grants (Incoming + Outgoing) --------------------
export const GrantsPanel = () => {
    const [side, setSide] = useState("incoming");
    return (
        <div data-testid="admin-grants-panel">
            <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Grants</h2>
                <div className="flex gap-1 bg-[#F4F1EA] rounded-full p-1" data-testid="grants-side-toggle">
                    <button onClick={() => setSide("incoming")} data-testid="grants-side-incoming" className={`px-4 py-1.5 rounded-full text-xs font-medium ${side === "incoming" ? "bg-[#E53935] text-white" : "text-[#4A5568]"}`}>Incoming (we apply for)</button>
                    <button onClick={() => setSide("outgoing")} data-testid="grants-side-outgoing" className={`px-4 py-1.5 rounded-full text-xs font-medium ${side === "outgoing" ? "bg-[#E53935] text-white" : "text-[#4A5568]"}`}>Outgoing (we award)</button>
                </div>
            </div>
            {side === "incoming" ? <GrantsIncoming /> : <GrantsOutgoing />}
        </div>
    );
};

const GrantsIncoming = () => {
    const [items, setItems] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ funder: "", amount_requested: "", purpose: "", status: "draft", deadline: "", notes: "" });
    const load = () => api.get("/admin/grants/incoming").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/grants/incoming", { ...form, amount_requested: parseFloat(form.amount_requested), deadline: form.deadline || null });
            toast.success("Saved");
            setShow(false);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    };

    return (
        <>
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-[#4A5568]">{items.length} applications · grants you've applied for</p>
                <button onClick={() => setShow(true)} data-testid="grants-in-add-btn" className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-5 py-2 text-sm font-medium">
                    <Plus className="w-4 h-4" /> Log application
                </button>
            </div>
            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="grants-in-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr><th className="text-left px-4 py-3 font-medium">Funder</th><th className="text-left px-4 py-3 font-medium">Purpose</th><th className="text-right px-4 py-3 font-medium">Requested</th><th className="text-right px-4 py-3 font-medium">Awarded</th><th className="text-left px-4 py-3 font-medium">Deadline</th><th className="text-left px-4 py-3 font-medium">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-[#4A5568]">No grant applications logged yet.</td></tr>}
                        {items.map((g) => (
                            <tr key={g.id} data-testid={`grant-in-${g.id}`}>
                                <td className="px-4 py-3 font-medium text-[#1C252E]">{g.funder}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{g.purpose}</td>
                                <td className="px-4 py-3 text-right font-mono">${(g.amount_requested || 0).toFixed(2)}</td>
                                <td className="px-4 py-3 text-right font-mono text-green-700">{g.amount_awarded ? `$${g.amount_awarded.toFixed(2)}` : "—"}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{g.deadline || "—"}</td>
                                <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[#F4F1EA] text-[#4A5568]">{(g.status || "").replace("_", " ")}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)} data-testid="grant-in-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-5"><h3 className="font-heading text-2xl">Log grant application</h3><button onClick={() => setShow(false)}><X className="w-4 h-4" /></button></div>
                        <form onSubmit={save} className="space-y-3">
                            <input required placeholder="Funder name" value={form.funder} onChange={(e) => setForm({ ...form, funder: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="grant-in-funder" />
                            <textarea required placeholder="Purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} rows={2} className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none" data-testid="grant-in-purpose" />
                            <div className="grid grid-cols-2 gap-3">
                                <input required type="number" step="0.01" placeholder="Amount requested" value={form.amount_requested} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} className="rounded-xl border border-black/10 px-4 py-3 font-mono" data-testid="grant-in-amount" />
                                <input type="date" placeholder="Deadline" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="rounded-xl border border-black/10 px-4 py-3" data-testid="grant-in-deadline" />
                            </div>
                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white" data-testid="grant-in-status">
                                {["draft", "submitted", "under_review", "awarded", "declined"].map((s) => <option key={s}>{s}</option>)}
                            </select>
                            <button type="submit" data-testid="grant-in-submit" className="w-full bg-[#E53935] text-white rounded-full py-3 font-medium">Save</button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

const GrantsOutgoing = () => {
    const [items, setItems] = useState([]);
    const load = () => api.get("/admin/grants/outgoing").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const markPaid = async (g) => {
        if (!window.confirm(`Mark grant to ${g.recipient_name} as paid? This posts a journal entry to the books.`)) return;
        try {
            await api.post(`/admin/grants/outgoing/${g.id}/mark-paid`);
            toast.success("Marked paid · JE posted");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const updateStatus = async (g, status) => {
        try {
            await api.patch(`/admin/grants/outgoing/${g.id}`, { ...g, status, amount_awarded: g.amount_awarded ?? g.amount_requested });
            toast.success("Updated");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    return (
        <>
            <p className="text-sm text-[#4A5568] mb-4">{items.length} micro-grant applications · teachers apply via the public form at <strong>/apply/grant</strong></p>
            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="grants-out-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr>
                            <th className="text-left px-4 py-3 font-medium">Recipient</th>
                            <th className="text-left px-4 py-3 font-medium">Category</th>
                            <th className="text-left px-4 py-3 font-medium">Purpose</th>
                            <th className="text-right px-4 py-3 font-medium">Requested</th>
                            <th className="text-left px-4 py-3 font-medium">Status</th>
                            <th className="text-right px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-[#4A5568]">No micro-grant applications yet.</td></tr>}
                        {items.map((g) => {
                            const urgent = g.urgency === "urgent";
                            return (
                            <tr key={g.id} data-testid={`grant-out-${g.id}`} className={urgent ? "bg-amber-50/60" : ""}>
                                <td className="px-4 py-3">
                                    <p className="font-medium text-[#1C252E]">{g.recipient_name}</p>
                                    {g.organization && <p className="text-xs text-[#4A5568]">{g.organization}</p>}
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {g.public_submission && <span className="text-[10px] uppercase tracking-wider font-bold text-[#E53935]">Public</span>}
                                        {g.requested_by_role && <span className="text-[10px] uppercase tracking-wider text-[#4A5568]">· {g.requested_by_role.replace(/_/g, " ")}</span>}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-xs">
                                    {g.need_category ? (
                                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#1C252E] capitalize">
                                            {g.need_category.replace(/_/g, " ")}
                                        </span>
                                    ) : <span className="text-[#4A5568]">—</span>}
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {urgent && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900">Urgent</span>}
                                        {!g.publicly_displayable && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700" title="Recipient asked NOT to publicly display this grant">Private</span>}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{g.purpose}</td>
                                <td className="px-4 py-3 text-right font-mono">${(g.amount_requested || 0).toFixed(2)}</td>
                                <td className="px-4 py-3">
                                    <select value={g.status} onChange={(e) => updateStatus(g, e.target.value)} className="rounded-full border border-black/10 px-3 py-1 text-xs bg-white" data-testid={`grant-out-status-${g.id}`}>
                                        {["submitted", "under_review", "approved", "denied", "paid"].map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    {g.status === "approved" && (
                                        <button onClick={() => markPaid(g)} data-testid={`grant-out-pay-${g.id}`} className="text-xs px-3 py-1.5 rounded-full bg-[#1C252E] text-white hover:bg-black">Mark paid</button>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};

// -------------------- Recurring subscriptions --------------------
export const SubscriptionsPanel = () => {
    const [subs, setSubs] = useState([]);
    useEffect(() => { api.get("/admin/subscriptions").then((r) => setSubs(r.data)); }, []);
    const active = subs.filter((s) => s.active);
    const monthly_commit = active.reduce((sum, s) => {
        if (s.frequency === "monthly") return sum + s.amount;
        if (s.frequency === "quarterly") return sum + s.amount / 3;
        if (s.frequency === "annually") return sum + s.amount / 12;
        return sum;
    }, 0);
    return (
        <div data-testid="admin-subscriptions">
            <div className="flex justify-between items-end mb-5">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Recurring donations</h2>
                    <p className="text-sm text-[#4A5568]">{active.length} active · ${monthly_commit.toFixed(2)}/mo committed</p>
                </div>
            </div>
            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="subs-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr><th className="text-left px-4 py-3 font-medium">Donor</th><th className="text-left px-4 py-3 font-medium">Email</th><th className="text-right px-4 py-3 font-medium">Amount</th><th className="text-left px-4 py-3 font-medium">Frequency</th><th className="text-right px-4 py-3 font-medium">Runs</th><th className="text-left px-4 py-3 font-medium">Next run</th><th className="text-left px-4 py-3 font-medium">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {subs.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-[#4A5568]">No recurring donors yet.</td></tr>}
                        {subs.map((s) => (
                            <tr key={s.id} data-testid={`sub-${s.id}`}>
                                <td className="px-4 py-3 font-medium text-[#1C252E]">{s.donor_name}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{s.donor_email}</td>
                                <td className="px-4 py-3 text-right font-mono">${s.amount.toFixed(2)}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568] capitalize">{s.frequency}</td>
                                <td className="px-4 py-3 text-right text-[#4A5568]">{s.runs_count}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{(s.next_run_at || "").slice(0, 10)}</td>
                                <td className="px-4 py-3">
                                    {s.active ? <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-700">Active</span> : <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-gray-200 text-gray-600">Cancelled</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="mt-4 text-xs text-[#4A5568]">⚠️ Mocked-paid until real PayPal/Stripe is wired. The scheduler still creates donation records and posts journal entries on schedule.</p>
        </div>
    );
};

// -------------------- Volunteer Hours + Skills --------------------
export const VolunteerHoursPanel = () => {
    const [vols, setVols] = useState([]);
    const [hours, setHours] = useState([]);
    const [editingVol, setEditingVol] = useState(null);
    const [hoursForm, setHoursForm] = useState({ volunteer_id: "", event: "", hours: "", date: new Date().toISOString().slice(0, 10), notes: "" });
    const [showHours, setShowHours] = useState(false);

    const load = () => {
        api.get("/admin/volunteers-with-stats").then((r) => setVols(r.data));
        api.get("/admin/volunteer-hours").then((r) => setHours(r.data));
    };
    useEffect(() => { load(); }, []);

    const saveHours = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/volunteer-hours", { ...hoursForm, hours: parseFloat(hoursForm.hours) });
            toast.success("Hours logged");
            setShowHours(false);
            setHoursForm({ volunteer_id: "", event: "", hours: "", date: new Date().toISOString().slice(0, 10), notes: "" });
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    };

    const saveSkills = async () => {
        const skills = (editingVol.skills_text || "").split(",").map((s) => s.trim()).filter(Boolean);
        try {
            await api.patch(`/admin/volunteers/${editingVol.id}/skills`, { skills, availability: editingVol.availability || null });
            toast.success("Skills updated");
            setEditingVol(null);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    };

    const totalHours = vols.reduce((s, v) => s + (v.total_hours || 0), 0);
    const totalInKind = vols.reduce((s, v) => s + (v.in_kind_value || 0), 0);

    return (
        <div data-testid="admin-volunteer-hours">
            <div className="flex justify-between items-end mb-5 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Volunteers · skills &amp; hours</h2>
                    <p className="text-sm text-[#4A5568]">{vols.length} volunteers · <strong>{totalHours.toFixed(1)}</strong> total hours · <strong>${totalInKind.toLocaleString()}</strong> in-kind value (Form 990)</p>
                </div>
                <button onClick={() => setShowHours(true)} data-testid="log-hours-btn" className="inline-flex items-center gap-2 bg-[#E53935] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#D32F2F]">
                    <Clock className="w-4 h-4" /> Log hours
                </button>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto mb-6">
                <table className="w-full text-sm" data-testid="vol-skills-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr><th className="text-left px-4 py-3 font-medium">Name</th><th className="text-left px-4 py-3 font-medium">Email</th><th className="text-left px-4 py-3 font-medium">Skills</th><th className="text-left px-4 py-3 font-medium">Availability</th><th className="text-right px-4 py-3 font-medium">Hours</th><th className="text-right px-4 py-3 font-medium">In-kind</th><th className="text-right px-4 py-3"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {vols.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-[#4A5568]">No volunteers yet.</td></tr>}
                        {vols.map((v) => (
                            <tr key={v.id} data-testid={`vol-row-${v.id}`}>
                                <td className="px-4 py-3 font-medium text-[#1C252E]">{v.name}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{v.email}</td>
                                <td className="px-4 py-3 text-xs">{(v.skills && v.skills.length) ? v.skills.map((s) => <span key={s} className="inline-block mr-1 mb-1 px-2 py-0.5 rounded-full bg-[#F2D8D5]/60 text-[#1C252E] text-[10px]">{s}</span>) : <span className="text-[#4A5568]">—</span>}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{v.availability || "—"}</td>
                                <td className="px-4 py-3 text-right font-mono">{(v.total_hours || 0).toFixed(1)}</td>
                                <td className="px-4 py-3 text-right font-mono text-[#4A5568]">${(v.in_kind_value || 0).toFixed(2)}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => setEditingVol({ ...v, skills_text: (v.skills || []).join(", ") })} data-testid={`vol-edit-skills-${v.id}`} className="text-xs text-[#E53935] hover:underline">Skills</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <p className="px-5 py-3 text-xs uppercase tracking-wider font-bold text-[#4A5568] bg-[#F4F1EA]">Recent hours logged</p>
                <table className="w-full text-sm">
                    <tbody className="divide-y divide-black/5">
                        {hours.length === 0 && <tr><td colSpan={4} className="px-6 py-8 text-center text-[#4A5568]">No hours logged yet.</td></tr>}
                        {hours.slice(0, 20).map((h) => (
                            <tr key={h.id}><td className="px-4 py-2 text-xs text-[#4A5568]">{h.date}</td><td className="px-4 py-2 text-[#1C252E]">{h.volunteer_name}</td><td className="px-4 py-2 text-xs text-[#4A5568]">{h.event}</td><td className="px-4 py-2 text-right font-mono">{h.hours.toFixed(1)} hrs</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showHours && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowHours(false)} data-testid="log-hours-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-5"><h3 className="font-heading text-2xl">Log hours</h3><button onClick={() => setShowHours(false)}><X className="w-4 h-4" /></button></div>
                        <form onSubmit={saveHours} className="space-y-3">
                            <select required value={hoursForm.volunteer_id} onChange={(e) => setHoursForm({ ...hoursForm, volunteer_id: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white" data-testid="hours-volunteer">
                                <option value="">— Volunteer —</option>
                                {vols.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                            </select>
                            <input required placeholder="Event" value={hoursForm.event} onChange={(e) => setHoursForm({ ...hoursForm, event: e.target.value })} className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="hours-event" />
                            <div className="grid grid-cols-2 gap-3">
                                <input required type="number" step="0.25" placeholder="Hours" value={hoursForm.hours} onChange={(e) => setHoursForm({ ...hoursForm, hours: e.target.value })} className="rounded-xl border border-black/10 px-4 py-3 font-mono" data-testid="hours-amount" />
                                <input required type="date" value={hoursForm.date} onChange={(e) => setHoursForm({ ...hoursForm, date: e.target.value })} className="rounded-xl border border-black/10 px-4 py-3" data-testid="hours-date" />
                            </div>
                            <textarea placeholder="Notes (optional)" value={hoursForm.notes} onChange={(e) => setHoursForm({ ...hoursForm, notes: e.target.value })} rows={2} className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none" />
                            <button type="submit" data-testid="hours-submit" className="w-full bg-[#E53935] text-white rounded-full py-3 font-medium">Log hours</button>
                        </form>
                    </div>
                </div>
            )}

            {editingVol && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingVol(null)} data-testid="vol-skills-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-5"><h3 className="font-heading text-2xl">{editingVol.name}'s skills</h3><button onClick={() => setEditingVol(null)}><X className="w-4 h-4" /></button></div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Skills (comma-separated)</label>
                                <textarea value={editingVol.skills_text || ""} onChange={(e) => setEditingVol({ ...editingVol, skills_text: e.target.value })} rows={3} placeholder="e.g. Spanish translator, baking, carpentry" className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none" data-testid="vol-skills-input" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5">Availability</label>
                                <input value={editingVol.availability || ""} onChange={(e) => setEditingVol({ ...editingVol, availability: e.target.value })} placeholder="e.g. Weekends, evenings" className="w-full rounded-xl border border-black/10 px-4 py-3" data-testid="vol-availability-input" />
                            </div>
                            <button onClick={saveSkills} data-testid="vol-skills-save" className="w-full bg-[#E53935] text-white rounded-full py-3 font-medium">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// -------------------- Contractor Applications --------------------
export const ContractorApplicationsPanel = () => {
    const [apps, setApps] = useState([]);
    const [active, setActive] = useState(null);
    const [notes, setNotes] = useState("");
    const load = () => api.get("/admin/contractor-applications").then((r) => setApps(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const review = async (status) => {
        try {
            await api.patch(`/admin/contractor-applications/${active.id}/review`, { status, review_notes: notes });
            toast.success(status === "approved" ? "Approved · promoted to vetted registry" : "Updated");
            setActive(null);
            setNotes("");
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const STATUS_COLOR = {
        new: "bg-blue-100 text-blue-700",
        under_review: "bg-amber-100 text-amber-700",
        approved: "bg-green-100 text-green-700",
        declined: "bg-red-100 text-red-700",
    };

    return (
        <div data-testid="admin-contractor-apps">
            <div className="flex justify-between items-end mb-5">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Contractor applications</h2>
                    <p className="text-sm text-[#4A5568]">{apps.length} applications · contractors apply at the public form <strong>/apply/contractor</strong></p>
                </div>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-x-auto">
                <table className="w-full text-sm" data-testid="apps-table">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr><th className="text-left px-4 py-3 font-medium">Business</th><th className="text-left px-4 py-3 font-medium">Trade</th><th className="text-left px-4 py-3 font-medium">Contact</th><th className="text-left px-4 py-3 font-medium">Submitted</th><th className="text-left px-4 py-3 font-medium">Status</th><th className="text-right px-4 py-3"></th></tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {apps.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-[#4A5568]">No applications yet.</td></tr>}
                        {apps.map((a) => (
                            <tr key={a.id} data-testid={`app-row-${a.id}`}>
                                <td className="px-4 py-3 font-medium text-[#1C252E]">{a.business_name}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{a.trade}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{a.contact_name}<br />{a.contact_email}</td>
                                <td className="px-4 py-3 text-xs text-[#4A5568]">{(a.submitted_at || "").slice(0, 10)}</td>
                                <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${STATUS_COLOR[a.status] || "bg-gray-100"}`}>{a.status.replace("_", " ")}</span></td>
                                <td className="px-4 py-3 text-right"><button onClick={() => { setActive(a); setNotes(a.review_notes || ""); }} data-testid={`app-review-${a.id}`} className="text-xs text-[#E53935] hover:underline">Review</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {active && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="app-review-modal">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-5">
                            <div>
                                <h3 className="font-heading text-2xl text-[#1C252E] font-medium">{active.business_name}</h3>
                                <p className="text-xs text-[#4A5568] mt-1">{active.trade} · submitted {(active.submitted_at || "").slice(0, 10)}</p>
                            </div>
                            <button onClick={() => setActive(null)}><X className="w-4 h-4" /></button>
                        </div>
                        <div className="space-y-3 text-sm">
                            <Row label="Contact" value={`${active.contact_name} · ${active.contact_email}${active.contact_phone ? ` · ${active.contact_phone}` : ""}`} />
                            <Row label="License #" value={active.license_number || "—"} />
                            <Row label="Insurance" value={active.insurance_carrier || "—"} />
                            <Row label="Years in business" value={active.years_in_business?.toString() || "—"} />
                            <Row label="References" value={active.references || "—"} />
                            <Row label="Conflict-of-interest disclosure" value={active.coi_disclosure} />
                            <Row label="Pricing notes" value={active.pricing_notes || "—"} />
                            <Row label="Why us" value={active.why_us || "—"} />
                            {active.promoted_to_contractor_id && (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-800">
                                    ✓ Approved &amp; promoted to vetted registry (contractor id: <code>{active.promoted_to_contractor_id.slice(0, 8)}…</code>)
                                </div>
                            )}
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Review notes (optional)" className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none" data-testid="app-review-notes" />
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => review("approved")} data-testid="app-approve-btn" className="flex-1 bg-green-700 text-white rounded-full py-2.5 text-sm font-medium hover:bg-green-800"><CheckCircle2 className="w-4 h-4 inline -mt-0.5 mr-1.5" />Approve &amp; promote</button>
                                <button onClick={() => review("under_review")} data-testid="app-review-btn" className="flex-1 bg-amber-600 text-white rounded-full py-2.5 text-sm font-medium hover:bg-amber-700">Under review</button>
                                <button onClick={() => review("declined")} data-testid="app-decline-btn" className="flex-1 bg-red-700 text-white rounded-full py-2.5 text-sm font-medium hover:bg-red-800">Decline</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Row = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-black/5">
        <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">{label}</p>
        <p className="col-span-2 text-[#1C252E] whitespace-pre-line">{value}</p>
    </div>
);
